import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Footer } from 'app/core/models/footer.model';
import { findClosestPrecedingHeader } from 'app/core/services/find-closest-preceding-header';
import { useShebaGtmService } from 'app/core/services/google-tag-manager/gtm.directive';
import { CURRENT_TEMPLATE } from 'app/core/services/google-tag-manager/use-current-template';
import { SharedService } from 'app/core/services/shared.service';
import { ArrowStartIcon } from '../../../../assets/icons/arrow-start/arrow-start';
import { ShebaButtonComponent } from '../button/button.component';
import { EmailSignupFormService } from './services/email-signup-form.service';

@Component({
  selector: 'sheba-email-signup-form',
  templateUrl: 'email-signup-form.component.html',
  providers: [EmailSignupFormService],
  imports: [ArrowStartIcon, ShebaButtonComponent, TranslateModule, ReactiveFormsModule, FormsModule],
  host: {
    class: ' max-md:w-[90%] flex flex-col place-items-center gap-11 lg:place-items-start',
    'mail-input': ''
  },
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmailSignupFormComponent {
  readonly #gtmService = useShebaGtmService();
  readonly #currentTemplate = inject(CURRENT_TEMPLATE);
  readonly #elementRef = inject(ElementRef);
  footerData = input<Footer>({} as Footer);

  readonly #emailSignupFormService = inject(EmailSignupFormService);
  readonly #router = inject(Router);
  lightMode = inject(SharedService).lightMode;

  emailSignupControl = new FormControl<string>('', [Validators.required, Validators.email]);
  showMessageRegister = false;

  messageType = signal<'success' | 'error' | null>(null);
  messageRef = viewChild<ElementRef<HTMLDivElement>>('messageRef');
  inputRef = viewChild<ElementRef<HTMLInputElement>>('inputRef');

  constructor() {
    effect(() => {
      this.messageRef()?.nativeElement.focus();
    });
  }

  showMessage(type: 'success' | 'error') {
    this.messageType.set(type);

    setTimeout(() => {
      this.messageType.set(null);
      this.emailSignupControl.reset();
      untracked(this.inputRef)?.nativeElement.focus();
    }, 4000);
  }

  emailSignupForm() {
    this.#emailSignupFormService
      .registerNewsletter({
        toEmail: this.emailSignupControl.value!,
        hospitalAlias: this.#router.url.replace(/^\/+/, '')
      })
      ?.subscribe((result) => {
        if (result.status === 200) {
          this.#gtmService.pushEvent({
            event: 'newsletter_success',
            eventInfo: {
              user_action: 'user_subscribes_newsletter',
              template: this.#currentTemplate() ?? null,
              header: findClosestPrecedingHeader(this.#elementRef.nativeElement) ?? 'newsletter'
            }
          });
          this.showMessage('success');
        } else if (result.status === 400) {
          this.showMessage('error');
        } else {
          this.showMessage('error');
        }
      });
  }
}
